import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { useQuery } from '@tanstack/react-query';
import { getTemplates } from '../../services/templateService';
import { Loader2, Download, CheckCircle2, XCircle } from 'lucide-react';
import { batchApplyTemplateAndDownload } from '../../services/batchTemplateService';

const BatchTemplateModal = ({ selectedProducts, onClose, onComplete }) => {
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, status: 'idle' });
    const [results, setResults] = useState(null);

    // Fetch templates
    const { data: templates = [] } = useQuery({
        queryKey: ['templates'],
        queryFn: () => getTemplates(),
    });

    const handleApplyAndDownload = async () => {
        if (!selectedTemplateId) {
            alert('Please select a template');
            return;
        }

        if (selectedProducts.length === 0) {
            alert('No products selected');
            return;
        }

        setIsProcessing(true);
        setProgress({ current: 0, total: selectedProducts.length, status: 'processing' });
        setResults(null);

        try {
            const downloadResults = await batchApplyTemplateAndDownload(
                selectedTemplateId,
                selectedProducts,
                (current, total, status) => {
                    setProgress({ current, total, status });
                }
            );

            setResults(downloadResults);
            setProgress({ ...progress, status: 'completed' });
        } catch (error) {
            console.error('Batch processing error:', error);
            setProgress({ ...progress, status: 'error' });
            alert('Error processing products: ' + error.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        if (!isProcessing) {
            setResults(null);
            setProgress({ current: 0, total: 0, status: 'idle' });
            onClose();
        }
    };

    const successCount = results?.success?.length || 0;
    const errorCount = results?.errors?.length || 0;

    return (
        <Dialog open={true} onOpenChange={(open) => !open && !isProcessing && handleClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Batch Template Application</DialogTitle>
                </DialogHeader>

                        <div className="mb-4">
                            <p className="text-sm text-gray-600">
                                {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
                            </p>
                        </div>

                        {!results && (
                            <>
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Template
                                    </label>
                                    <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-2">
                                        {templates.length === 0 ? (
                                            <p className="text-sm text-gray-500 py-4 text-center">
                                                No templates available. Create a template first.
                                            </p>
                                        ) : (
                                            templates.map((template) => (
                                                <div
                                                    key={template.id}
                                                    onClick={() => setSelectedTemplateId(template.id)}
                                                    className={`p-3 rounded-md cursor-pointer transition-colors border ${
                                                        selectedTemplateId === template.id
                                                            ? 'bg-[#8E0DFF]/10 border-[#8E0DFF]'
                                                            : 'bg-white hover:bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-semibold text-sm text-gray-900">
                                                                {template.name}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {new Date(template.createdAt).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                        {selectedTemplateId === template.id && (
                                                            <CheckCircle2 className="w-5 h-5 text-[#8E0DFF]" />
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {isProcessing && (
                                    <div className="mb-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-700">
                                                Processing: {progress.current} / {progress.total}
                                            </span>
                                            <span className="text-sm text-gray-500">{progress.status}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div
                                                className="bg-[#8E0DFF] h-2.5 rounded-full transition-all duration-300"
                                                style={{
                                                    width: `${(progress.current / progress.total) * 100}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={handleClose}
                                        disabled={isProcessing}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleApplyAndDownload}
                                        disabled={!selectedTemplateId || isProcessing}
                                        className="bg-[#8E0DFF] hover:bg-[#7A0BE6] flex items-center gap-2"
                                    >
                                        {isProcessing ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4" />
                                                Apply & Download All
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </>
                        )}

                        {results && (
                            <div className="space-y-4">
                                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                                    <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                                        <h3 className="font-semibold text-green-900">
                                            Successfully processed: {successCount} product{successCount !== 1 ? 's' : ''}
                                        </h3>
                                    </div>
                                    {results.success && results.success.length > 0 && (
                                        <ul className="text-sm text-green-700 space-y-1 max-h-32 overflow-y-auto">
                                            {results.success.map((item, idx) => (
                                                <li key={idx} className="flex items-center gap-2">
                                                    <span className="w-2 h-2 bg-green-600 rounded-full" />
                                                    {item.productTitle || `Product ${idx + 1}`}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {errorCount > 0 && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                                        <div className="flex items-center gap-2 mb-2">
                                            <XCircle className="w-5 h-5 text-red-600" />
                                            <h3 className="font-semibold text-red-900">
                                                Errors: {errorCount} product{errorCount !== 1 ? 's' : ''}
                                            </h3>
                                        </div>
                                        <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                                            {results.errors.map((item, idx) => (
                                                <li key={idx} className="flex items-start gap-2">
                                                    <span className="w-2 h-2 bg-red-600 rounded-full mt-1.5" />
                                                    <div>
                                                        <span className="font-medium">
                                                            {item.productTitle || `Product ${idx + 1}`}
                                                        </span>
                                                        <span className="text-red-600 ml-2">
                                                            {item.error}
                                                        </span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="flex justify-end">
                                    <Button
                                        onClick={() => {
                                            handleClose();
                                            onComplete();
                                        }}
                                        className="bg-[#8E0DFF] hover:bg-[#7A0BE6]"
                                    >
                                        Done
                                    </Button>
                                </div>
                            </div>
                        )}
            </DialogContent>
        </Dialog>
    );
};

export default BatchTemplateModal;
